import { useCallback } from "react";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import moment from "moment";
import {
  getDocs,
  addDoc,
  updateDoc,
  doc as firestoreDoc,
  query,
  where,
} from "firebase/firestore";
import { getSubCollection } from "../utils/getSubCollection";

moment.locale("es");

/* ============================================================
   Configuración por tipo de documento
   - Las claves aquí (cotizaciones, remisiones, pedidos, orden)
     son los valores que debes pasar en `docType` al usar el hook.
   - `collection` es la colección dentro de /usuarios/{ownerUid}/...
   - `keyField` es el campo único por el que detectamos si existe
   - `excelKey` es la columna del Excel que agrupa filas (PO number,
     quote number, etc.).
   - `mapRow(row, maps, formatExcelDate)` transforma una fila del
     Excel a la estructura del documento en Firestore.
   ============================================================*/
const docConfigs = {
  cotizaciones: {
    collection: "cotizaciones",
    keyField: "numeroCotizacion",
    excelKey: "Quote Number", // <-- ajusta al nombre real de tu Excel
    mapRow: (row, maps, formatExcelDate, ownerUid) => ({
      archivos: [],
      area: row["Area"] || "",
      asunto: row["Subject"] || "",
      atencion: row["Attention"] || "",
      cliente: maps.clientes[row["Client Name"]] || null,
      createdAt: { _methodName: "serverTimestamp" },
      descuento: parseFloat(row["Discount"] || 0) || 0,
      divisa: row["Currency"] || "USD",
      estado: "Pendiente",
      fechaCotizacion: row["Quote Date"]
        ? formatExcelDate(row["Quote Date"])
        : null,
      fechaVencimiento: row["Due Date"]
        ? formatExcelDate(row["Due Date"])
        : null,
      nombreCliente: row["Client Name"] || "",
      notas: row["Notes"] || "",
      numeroCotizacion: row["Quote Number"] || "",
      ownerId: ownerUid,
      planta: row["Plant"] || "",
      productosSeleccionados: row["Item Name"]
        ? [
            {
              id: crypto.randomUUID(),
              nombre: row["Item Name"] || "",
              descripcion: row["Item Desc"] || "",
              cantidad: parseFloat(row["Quantity"] || 0) || 0,
              tarifa: parseFloat(row["Unit Price"] || 0) || 0,
              importe: parseFloat(row["Amount"] || 0) || 0,
              descuento: parseFloat(row["Item Discount"] || 0) || 0,
              iva: 0.16,
              numeroDeParteCliente: row["Item.CF.NumeroParteCliente"] || "",
              tipoDescuento: row["Discount Type"] || "%",
            },
          ]
        : [],
      referencia: row["Reference"] || "",
      terminos: row["Terms"] || "",
      total:
        parseFloat(
          (row["Total Amount"] || "0").toString().replace(/[^0-9.-]+/g, "")
        ) || 0,
      vendedorCorreo: row["Seller Email"] || "",
      vendedorNombre: row["Seller Name"] || "",
    }),
  },

  remisiones: {
    collection: "remisiones",
    keyField: "numeroRemision",
    excelKey: "Remission Number", // <-- ajusta al nombre real de tu Excel
    mapRow: (row, maps, formatExcelDate, ownerUid) => ({
      archivos: [],
      area: row["Area"] || "",
      asunto: row["Subject"] || "",
      atencion: row["Attention"] || "",
      cliente: maps.clientes[row["Client Name"]] || null,
      condicion: row["Terms"] || "",
      createdAt: { _methodName: "serverTimestamp" },
      descuento: parseFloat(row["Discount"] || 0) || 0,
      divisa: row["Currency"] || "USD",
      estado: row["Status"] || "Pendiente",
      fechaRemision: row["Remission Date"]
        ? formatExcelDate(row["Remission Date"])
        : null,
      incoterm: row["Incoterm"] || "",
      nombreCliente: row["Client Name"] || "",
      notas: row["Notes"] || "",
      numeroCotizacion: row["Quote Number"] || "",
      numeroRemision: row["Remission Number"] || "",
      observacion: row["Observation"] || "",
      ownerId: ownerUid,
      planta: row["Plant"] || "",
      productosSeleccionados: row["Item Name"]
        ? [
            {
              id: crypto.randomUUID(),
              nombre: row["Item Name"] || "",
              descripcion: row["Item Desc"] || "",
              cantidad: parseFloat(row["Quantity"] || 0) || 0,
              tarifa: parseFloat(row["Unit Price"] || 0) || 0,
              importe: parseFloat(row["Amount"] || 0) || 0,
              descuento: parseFloat(row["Item Discount"] || 0) || 0,
              iva: 0.16,
              numeroDeParteCliente: row["Item.CF.NumeroParteCliente"] || "",
              tipoDescuento: row["Discount Type"] || "%",
            },
          ]
        : [],
      referencia: row["Reference"] || "",
      terminos: row["Terms"] || "",
      total:
        parseFloat(
          (row["Total Amount"] || "0").toString().replace(/[^0-9.-]+/g, "")
        ) || 0,
      vendedorCorreo: row["Seller Email"] || "",
      vendedorNombre: row["Seller Name"] || "",
    }),
  },

  // Orden de compra del cliente -> colección 'pedidos'
  pedidos: {
    collection: "pedidos",
    keyField: "numeroOrdenDeCompra",
    excelKey: "PO Number", // <-- cámbialo si en tu Excel se llama diferente
    mapRow: (row, maps, formatExcelDate, ownerUid) => ({
      archivos: [],
      area: "",
      asunto: "",
      cliente: maps.clientes[row["Customer"]] || null,
      createdAt: { _methodName: "serverTimestamp" }, // se sobrescribe en Firestore
      descuento: 0,
      divisa: "MXN",
      estado: "Pendiente",
      fechaCotizacion: row["Quote Date"]
        ? formatExcelDate(row["Quote Date"])
        : null,
      fechaFinRango: null,
      fechaInicioRango: null,
      fechaOrden: row["PO Date"] ? formatExcelDate(row["PO Date"]) : null,
      fechaVencimiento: null,
      nombreCliente: row["Customer"] || "",
      notas: "",
      numeroCotizacion: row["Quote Number"] || "",
      numeroOrdenDeCompra: row["PO Number"] || "",
      ownerId: ownerUid,
      planta: "",
      referencia: row["PurchaseOrder"] || "",
      terminos: "",
      total:
        parseFloat(
          (row["Total Amount"] || "0").toString().replace(/[^0-9.-]+/g, "")
        ) || 0,
      usuario: "",
      vendedorCorreo: "",
      vendedorNombre: "",
      productosSeleccionados: row["Item Name"]
        ? [
            {
              id: crypto.randomUUID(),
              nombre: row["Item Name"] || "",
              descripcion: row["Item Desc"] || "",
              modelo: row["Item.CF.Modelo"] || "",
              cantidad: parseFloat(row["Quantity"] || 0) || 0,
              tarifa: parseFloat(row["Unit Price"] || 0) || 0,
              importe: parseFloat(row["Amount"] || 0) || 0,
              descuento: 0,
              iva: 0.16,
              numeroDeParteCliente: "",
              tipoDescuento: "%",
            },
          ]
        : [],
    }),
  },

  // Orden de compra del proveedor -> colección 'orden'
  ordenes: {
    collection: "ordenes",
    keyField: "numeroOrden",
    excelKey: "PO Number", // <-- ajusta al nombre real de tu Excel
    mapRow: (row, maps, formatExcelDate, ownerUid) => ({
      archivos: [],
      comprador: row["Buyer"] || "",
      condicion: row["Terms"] || "",
      cotizacion: row["Quote Number"] || "",
      createdAt: { _methodName: "serverTimestamp" },
      descuento: parseFloat(row["Discount"] || 0) || 0,
      divisa: row["Currency"] || "USD",
      estado: "Pendiente",
      fechaOrden: row["PO Date"] ? formatExcelDate(row["PO Date"]) : null,
      fechaVencimiento: row["Due Date"]
        ? formatExcelDate(row["Due Date"])
        : null,
      incoterm: row["Incoterm"] || "",
      nombreProveedor: row["Vendor Name"] || "",
      notas: row["Notes"] || "",
      numeroOrden: row["PO Number"] || "",
      ownerId: ownerUid,
      productosSeleccionados: row["Item Name"]
        ? [
            {
              id: crypto.randomUUID(),
              nombre: row["Item Name"] || "",
              descripcion: row["Item Desc"] || "",
              cantidad: parseFloat(row["Quantity"] || 0) || 0,
              tarifa: parseFloat(row["Unit Price"] || 0) || 0,
              importe: parseFloat(row["Amount"] || 0) || 0,
              descuento: parseFloat(row["Item Discount"] || 0) || 0,
              iva: 0.16,
              numeroDeParteCliente: row["Item.CF.NumeroParteCliente"] || "",
              tipoDescuento: row["Discount Type"] || "%",
            },
          ]
        : [],
      proveedor: maps.proveedores[row["Vendor Name"]] || null,
      referencia: row["Reference"] || "",
      terminos: row["Terms"] || "",
      total:
        parseFloat(
          (row["Total Amount"] || "0").toString().replace(/[^0-9.-]+/g, "")
        ) || 0,
    }),
  },
};

/* ============================================================
   Hook principal: useFileUpload
   - ownerUid: id del equipo/usuario donde están las subcolecciones
   - setData: setter del estado local donde guardar la lista final
   - docType: una de las claves definidas en docConfigs
   - options: { accept: ".xlsx,.xls" } (opcional)
   ============================================================*/
export default function useFileUpload({
  ownerUid,
  setData,
  docType,
  options = {},
}) {
  const handleFileUpload = useCallback(
    async (event) => {
      const file = event?.target?.files?.[0];
      if (!file) {
        return Swal.fire("Error", "No se seleccionó ningún archivo.", "error");
      }

      const config = docConfigs[docType];
      if (!config) {
        return Swal.fire(
          "Error",
          `No existe configuración para ${docType}`,
          "error"
        );
      }

      const reader = new FileReader();

      const originalConsoleError = console.error;
      console.error = (msg, ...args) => {
        if (typeof msg === "string" && msg.includes("Bad uncompressed size"))
          return;
        originalConsoleError(msg, ...args);
      };

      const formatExcelDate = (excelDate) => {
        let m;
        if (typeof excelDate === "string") {
          m = moment(
            excelDate,
            ["YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY"],
            true
          );
        } else if (excelDate instanceof Date) {
          m = moment(excelDate);
        } else if (typeof excelDate === "number") {
          m = moment(new Date(Math.round((excelDate - 25569) * 86400 * 1000)));
        } else return null;
        return m.isValid() ? m.toISOString() : null;
      };

      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const wb = XLSX.read(data, {
            type: "array",
            cellDates: true,
            raw: true,
          });

          if (!wb?.SheetNames?.length) throw new Error("Sin hojas válidas.");

          const sheet = wb.Sheets[wb.SheetNames[0]];
          if (!sheet) throw new Error("No se pudo leer la hoja.");

          const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
          if (!rows.length) throw new Error("Sin datos en el archivo.");

          // Cargamos mapas de clientes y proveedores (si existen)
          const clientesCol = getSubCollection(ownerUid, "clientes");
          const proveedoresCol = getSubCollection(ownerUid, "proveedores");

          const [clientesSnap, proveedoresSnap] = await Promise.all([
            getDocs(clientesCol),
            getDocs(proveedoresCol),
          ]);

          const clientesMap = {};
          clientesSnap.forEach((d) => {
            const dat = d.data();
            if (dat && dat.nombreCliente) clientesMap[dat.nombreCliente] = d.id;
          });

          const proveedoresMap = {};
          proveedoresSnap.forEach((d) => {
            const dat = d.data();
            if (dat && dat.nombreProveedor)
              proveedoresMap[dat.nombreProveedor] = d.id;
            // también intentamos con 'Supplier' o 'name'
            if (dat && dat.name && !proveedoresMap[dat.name])
              proveedoresMap[dat.name] = d.id;
          });

          const maps = { clientes: clientesMap, proveedores: proveedoresMap };

          // Referencia a la subcolección destino
          const colRef = getSubCollection(ownerUid, config.collection);

          // Agrupar filas por la clave definida en config.excelKey
          const agrup = rows.reduce((acc, row) => {
            const key = row[config.excelKey];
            if (!key) return acc;
            const keyStr = String(key).trim();
            if (!acc[keyStr])
              acc[keyStr] = config.mapRow(row, maps, formatExcelDate, ownerUid);
            else {
              // Agregamos líneas de producto si ya existe la clave
              const nombreProducto = row["Item Name"] || row["Item"] || "";
              if (nombreProducto) {
                acc[keyStr].productosSeleccionados =
                  acc[keyStr].productosSeleccionados || [];
                acc[keyStr].productosSeleccionados.push({
                  nombre: nombreProducto,
                  cantidad: parseFloat(row["Quantity"] || row["Qty"] || 0) || 0,
                  tarifa:
                    parseFloat(
                      row["Item Price"] || row["Rate"] || row["Unit Price"] || 0
                    ) || 0,
                  importe:
                    parseFloat(row["Item Total"] || row["Amount"] || 0) || 0,
                });
              }

              // Si hay una columna 'Total' y es > 0 puede sobrescribir total principal
              const rawTot = row["Total"] || row["Total Amount"] || "";
              const tot =
                typeof rawTot === "string"
                  ? parseFloat(rawTot.replace(/[^0-9.-]+/g, ""))
                  : rawTot || 0;
              if (tot > 0) acc[keyStr].total = tot;
            }
            return acc;
          }, {});

          // Insertar / actualizar en Firestore
          const nuevas = [];
          const actualizadas = [];

          for (const key in agrup) {
            const docData = agrup[key];
            const q = query(colRef, where(config.keyField, "==", key));
            const snap = await getDocs(q);

            if (snap.empty) {
              await addDoc(colRef, docData);
              nuevas.push(key);
            } else {
              // actualizamos el primer documento que coincida
              snap.forEach(async (d) => {
                await updateDoc(firestoreDoc(colRef, d.id), docData);
              });
              actualizadas.push(key);
            }
          }

          // Obtenemos la lista actualizada desde Firestore para mantener el estado sincronizado
          const nuevoSnap = await getDocs(colRef);
          const nuevaLista = nuevoSnap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }));
          setData(nuevaLista);

          Swal.fire(
            "Importación exitosa",
            `${nuevas.length} agregado(s), ${actualizadas.length} actualizado(s).`,
            "success"
          );
        } catch (err) {
          console.error("Error procesando archivo:", err);
          Swal.fire("Error", err?.message || String(err), "error");
        } finally {
          // restaurar consola
          console.error = originalConsoleError;
        }
      };

      reader.onerror = () => {
        console.error = originalConsoleError;
        Swal.fire("Error lectura", "No se pudo leer el archivo.", "error");
      };

      reader.readAsArrayBuffer(file);
    },
    [ownerUid, setData, docType]
  );

  return handleFileUpload;
}
